import { Fragment, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import getLang from 'I18n/utils/getLang';
import { LORAX, KOALA, GREAT_WHITE } from 'HubStyleTokens/colors';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import UISection from 'UIComponents/section/UISection';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UILink from 'UIComponents/link/UILink';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import * as Sync from '../constants/dataSync/Sync';
import SharedDataCard from './SharedDataCard';
import { getDataSyncProductUrl } from '../../common/utils/urls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SYNC_DIRECTION_ICON_SIZE = 24;
const EmptyComponent = () => {
  return null;
};
const DataSyncBadge = styled(UIBadge).withConfig({
  displayName: "SharedData__DataSyncBadge",
  componentId: "sc-497mdi-0"
})(["background-color:", ";height:18px;margin-bottom:20px;&:hover{background-color:", ";}"], KOALA, GREAT_WHITE);
const SyncDirectionIcon = ({
  sync
}) => {
  let iconName;
  switch (sync) {
    case Sync.INBOUND:
      iconName = 'dataSyncRight';
      break;
    case Sync.OUTBOUND:
      iconName = 'dataSyncLeft';
      break;
    case Sync.BOTH:
      iconName = 'dataSync';
      break;
    default:
  }
  if (iconName) {
    return /*#__PURE__*/_jsx(UIIcon, {
      name: iconName,
      color: LORAX,
      size: SYNC_DIRECTION_ICON_SIZE
    });
  } else {
    return null;
  }
};
function SharedData({
  dataSyncHowItWorks,
  dataSyncInfo,
  dataSyncs,
  iconUrl,
  name,
  onTrackInteraction,
  opsHub
}) {
  const trackInteraction = useCallback(action => onTrackInteraction(action, 'overview'), [onTrackInteraction]);
  const renderedDataSyncInfo = dataSyncInfo && !opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("b", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-ui-apps-core.detail.overview.sharedData.moreInfoText`
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(UIBreakString, {
        children: dataSyncInfo
      })
    })]
  }) : null;
  const renderedHowItWorks = dataSyncHowItWorks && opsHub ? /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx("p", {
      className: "m-bottom-1",
      children: /*#__PURE__*/_jsx("b", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-ui-apps-core.detail.overview.sharedData.howItWorks`
        })
      })
    }), /*#__PURE__*/_jsx(UIBreakString, {
      children: /*#__PURE__*/_jsx(SanitizedText, {
        body: dataSyncHowItWorks
      })
    })]
  }) : null;
  const renderedDataSyncs = useMemo(() => {
    return /*#__PURE__*/_jsx("div", {
      children: dataSyncs.map((dataSync, index) => /*#__PURE__*/_jsx(SharedDataCard, {
        dataSync: dataSync,
        iconUrl: iconUrl,
        name: name,
        onToggleOpen: () => trackInteraction('toggle field mapping section open'),
        onOpenToolTip: () => trackInteraction('hover shared data description'),
        opsHub: opsHub,
        SyncIcon: SyncDirectionIcon
      }, `data-sync-card-${index}`))
    });
  }, [dataSyncs, iconUrl, name, opsHub, trackInteraction]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [opsHub && /*#__PURE__*/_jsx(UILink, {
      href: getDataSyncProductUrl(getLang()),
      onClick: () => trackInteraction('click data sync badge'),
      external: true,
      ExternalIcon: EmptyComponent,
      children: /*#__PURE__*/_jsx(DataSyncBadge, {
        use: "koala",
        children: /*#__PURE__*/_jsx(UIMediaObject, {
          itemLeft: /*#__PURE__*/_jsx(UIIcon, {
            name: "dataSync",
            size: "xxs",
            color: LORAX,
            style: {
              marginTop: 1
            }
          }),
          spacing: "none",
          style: {
            marginTop: 1
          },
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-apps-core.detail.overview.sharedData.badge`
          })
        })
      })
    }), renderedHowItWorks, /*#__PURE__*/_jsx(UISection, {
      children: renderedDataSyncs
    }), renderedDataSyncInfo]
  });
}
export default SharedData;